import type { FormEvent, JSX } from 'react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCreateLearningSpace } from '@/features/learning-spaces/hooks';
import { ApiError } from '@/lib/api/client';

export function CreateLearningSpaceDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreated: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const create = useCreateLearningSpace();

  function reset(): void {
    setName('');
    setDescription('');
    setValidationError(null);
    create.reset();
  }

  function handleOpenChange(next: boolean): void {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    if (name.trim().length === 0) {
      setValidationError('Nama ruang belajar tidak boleh kosong.');
      return;
    }

    create.mutate(
      {
        name: name.trim(),
        description: description.trim().length > 0 ? description.trim() : null,
      },
      {
        onSuccess: () => {
          onCreated();
          handleOpenChange(false);
        },
      },
    );
  }

  const apiErrorMessage =
    create.error instanceof ApiError
      ? create.error.message
      : create.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tambah Ruang Belajar</DialogTitle>
          <DialogDescription>
            Ruang belajar mengelompokkan kelas-kelas di organisasi ini.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="learning-space-name">Nama</Label>
            <Input
              id="learning-space-name"
              type="text"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              autoComplete="off"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="learning-space-description">Deskripsi (opsional)</Label>
            <Textarea
              id="learning-space-description"
              value={description}
              onChange={(event) => {
                setDescription(event.target.value);
              }}
            />
          </div>

          {validationError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
